<script lang="ts">
	import { Button, Input, Loader } from '@Shelly/shelly-design-system/index';
	import ValidateVatAddress from 'src/components/Maps/autocomplete/ValidateVATAddress.svelte';
	import { format } from 'svelte-i18n';
	import { paypal } from 'src/services/http';
	import { get } from 'svelte/store';
	import { ApiError } from 'src/services/utils/api-error';

	export let selectedCountry, name, vat, address, onError;
	$: vat = vat?.replace(/\s/g, '');

	const _format = get(format);

	let validVat = '';
	let vatError = '';
	let companyNameError = '';
	let addressError = '';
	let isLoading = false;
	let errorStatusCode = '';
	let timeout = 10000;
	let counter = 0;
	let canEditCompanyInfo = false;

	$: onError =
		validVat !== vat || errorStatusCode || /^(---|\s*)$/.test(address) || /^(---|\s*)$/.test(name);

	$: if (selectedCountry) {
		canEditCompanyInfo = false;
	}
	$: disableCompanyNameAddress = !!vatError || isLoading || !canEditCompanyInfo;

	export const getBillingDetails = () => {
		return {
			name,
			vat,
			address,
		};
	};

	const validate = (vat, name, address) => {
		if (vat === '') {
			vatError = _format('invoice_details_error_required_field', {
				default: 'This field is required',
			});
			onError = true;
		}

		if (/^(---|\s*)$/.test(name)) {
			companyNameError = _format('invoice_details_error_company_name', {
				default: 'This field is required. Please, add company name',
			});
			onError = true;
		}

		if (/^(---|\s*)$/.test(address)) {
			addressError = _format('invoice_details_error_company_address', {
				default: 'This field is required. Please, add company address',
			});
			onError = true;
		}
	};

	$: validate(vat, name, address);

	const handleVATinput = async (_timeout) => {
		isLoading = true;
		name = '';
		address = '';
		errorStatusCode = '';
		const trimmedVat = vat.replace(/\s+/g, '');
		try {
			const result = await paypal.get(
				`api/invoice_data/check-vat/${selectedCountry.value}/${trimmedVat}`,
				{
					timeout: _timeout,
				},
			);

			validVat = '';

			if (!result.name || !result.address) {
				throw new ApiError('Invalid VAT number', 500);
			}

			validVat = trimmedVat;

			name = result.name === '---' ? '' : result.name;
			address = result.address === '---' ? '' : result.address;

			if (result.name === '---' || result.address === '---') {
				canEditCompanyInfo = true;
			}

			isLoading = false;
			vatError = '';
			timeout = 10000;

			return;
		} catch (error) {
			// timeout is needed because the service for VAT requests doesn't return response every time and we need to send it more times for better UI
			counter = counter + 1;
			if (counter < 4) {
				timeout = timeout + 10000;
				handleVATinput(timeout);
			} else {
				counter = -1;
				vatError =
					error.status === 400
						? $format('error_premium_incorrect_vat', {
								default: 'VAT number is not correct. Please, try again',
							})
						: $format('error_premium_server_error', {
								default: 'Server error.',
							});
				name = '';
				address = '';
				isLoading = false;
				timeout = 10000;
			}
		}
	};
</script>

<Input
	aria-labelledby="input-uic1ibc"
	class="mb-m"
	icon="fas fa-file-invoice"
	label={$format('alert_premium_invoice_details_check_vat', {
		default: 'Please, check VAT number',
	})}
	bind:value={vat}
	error={vatError ||
		(validVat !== '' && vat !== validVat
			? $format('error_premium_incorrect_vat', {
					default: 'VAT number is not correct! Please, try again',
				})
			: '')}
	on:click={() => (vatError = '')}
	on:input={() => (vatError = '')}
	disabled={isLoading}
	id="input-uic1ibc"
/>

<Button
	on:click={() => handleVATinput(timeout)}
	variant="secondary"
	size="m"
	disabled={isLoading || !vat}
	class="mb-m mt-0"
	>{$format('button_invoice_details_check_vat', {
		default: 'Check VAT',
	})}
</Button>

{#if isLoading}
	<Loader size="m" />
{/if}

{#if canEditCompanyInfo}
	<Input
		aria-labelledby="input-46fbsfg"
		class="mb-m"
		icon="fas fa-building"
		label={$format('premium_invoice_details_company_name', {
			default: 'Company Name',
		})}
		bind:value={name}
		error={companyNameError}
		on:click={() => (companyNameError = '')}
		on:input={() => {
			companyNameError = '';
		}}
		disabled={disableCompanyNameAddress}
		id="input-46fbsfg"
	/>
	<ValidateVatAddress countryCode={selectedCountry.value} bind:address bind:addressError />
{:else}
	<Input
		class="mb-m"
		icon="fas fa-building"
		label={$format('premium_invoice_details_company_name', {
			default: 'Company Name',
		})}
		bind:value={name}
		disabled={disableCompanyNameAddress}
		id="input-tf4ydy9"
		aria-labelledby="input-tf4ydy9"
	/>
	<Input
		class="mb-s"
		icon="fas fa-home"
		label={$format('premium_invoice_details_address', {
			default: 'Address name',
		})}
		bind:value={address}
		disabled={disableCompanyNameAddress}
		id="input-f1zb5qx"
		aria-labelledby="input-f1zb5qx"
	/>
{/if}
